import { PlayerStatus } from '@tunein/web-player-manager';
import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { player } from 'src/client/player';
import MessagePopoverWithTail from 'src/common/components/messagePopoverWithTail/MessagePopoverWithTail';
import { DISCORD_PLAYBACK_DISABLED_TEXT } from 'src/common/components/player/actionDrawer/DiscordControls/constants';
import PopOutButton from 'src/common/components/shared/button/PopOutButton';
import CircularProgress from 'src/common/components/shared/CircularProgress';
import { playerStatuses } from 'src/common/constants/playerStatuses';
import connectWithPlayer from 'src/common/decorators/connectWithPlayer';
import useMobilePopout from 'src/common/hooks/useMobilePopout';
import { useStores } from 'src/common/providers';
import { selectDiscordState, selectIsDiscord, selectIsMobile } from 'src/common/selectors/app';
import cssVariables from 'src/common/styles/variables';
import { isMedium } from 'src/common/utils/breakpoints';
import { getPlayerManagerStatusSvgIcon } from 'src/common/utils/playerStatusToIconMap';
import css from './player-play-button.module.scss';

export const PlayButton = ({
  breakpoint,
  enableDiscordControlTooltip,
  disablePreroll,
  customFill,
  onClick,
  progressColor,
  customHeight,
  customWidth,
  guideItem: guideItemFromProps,
  className = '',
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const isDiscord = useSelector(selectIsDiscord);
  const discordState = useSelector(selectDiscordState);
  const isMobile = useSelector(selectIsMobile);
  const { canShowPopover, handleTargetClick } = useMobilePopout(isMobile, 3000);
  const { playerStore } = useStores();
  const metadata = playerStore.metadata();
  const popout = playerStore.popout();
  const rejectReason = metadata?.rejectReason;
  const currentGuideItem = playerStore.currentGuideItem();
  const playerGuideId = currentGuideItem?.playableGuideId;
  const guideItem = guideItemFromProps || currentGuideItem;
  const guideIdToPlay =
    guideItem?.playableGuideId || guideItem?.actions?.play?.guideId || guideItem?.guideId;
  const canScrub = !metadata?.isUnboundedStream;
  const playerStatus = playerStore.playerStatus();
  const isMediumBreakpoint = isMedium(breakpoint);
  const predeterminedHeight = isMediumBreakpoint ? 45 : 40;
  const predeterminedWidth = isMediumBreakpoint ? 45 : 40;
  const height = customHeight || predeterminedHeight;
  const width = customWidth || predeterminedWidth;
  const isPopoverOpen = Boolean(
    isDiscord &&
      (!discordState?.canControlPlayback || (rejectReason && guideIdToPlay === playerGuideId)) &&
      ((isMobile && canShowPopover) || (!isMobile && isHovering)),
  );

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handlePlayButtonClick = () => {
    if (onClick) {
      onClick();
    }

    player.onClick(guideItem, { skipPreroll: disablePreroll });
  };

  const handleRecoveringKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Space would otherwise scroll the page, which a native button doesn't do.
      event.preventDefault();
      handlePlayButtonClick();
    }
  };

  // Background recovery can hold this state for minutes, so unlike the loading spinner below it
  // stays interactive — clicking it stops the failing session instead of trapping the listener
  // behind a dead control. (Interim treatment; a first-class reconnecting state is proposed.)
  if (playerStatus === PlayerStatus.RECOVERING) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: keeps the wrapper and styles the other states use
      <div
        // There's no localized copy for the player's controls; their icons are titled in English too.
        aria-label="Stop"
        className={css.playButtonWrapper}
        data-testid="player-status-recovering"
        onClick={handlePlayButtonClick}
        onKeyDown={handleRecoveringKeyDown}
        role="button"
        tabIndex={0}
      >
        <CircularProgress
          id="playerLoadingSpinner"
          className={className}
          size={width}
          color={progressColor || cssVariables['--secondary-color-1']}
          thickness={2}
        />
      </div>
    );
  }

  if (playerStatus === PlayerStatus.LOADING || playerStatus === PlayerStatus.WAITING) {
    return (
      <CircularProgress
        id="playerLoadingSpinner"
        className={className}
        data-testid={`player-status-${playerStatus?.toLowerCase()}`}
        size={width}
        color={progressColor || cssVariables['--secondary-color-1']}
        thickness={2}
      />
    );
  }

  if ((playerStatus && playerStatus === playerStatuses.popout) || popout.showButton) {
    return (
      <div className={className} data-testid="popoutButtonWrapper">
        <PopOutButton dataTestId="player-status-popout" width={`${width}`} height={`${height}`} />
      </div>
    );
  }

  const PlayButtonStateRepresentation = getPlayerManagerStatusSvgIcon(playerStatus, canScrub);

  if (!PlayButtonStateRepresentation) {
    return null;
  }

  return (
    <div
      className={classNames(css.playButtonWrapper, {
        [css.isDisabled]: isDiscord && guideIdToPlay === playerGuideId && rejectReason,
      })}
      onClick={handleTargetClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {enableDiscordControlTooltip && (
        <MessagePopoverWithTail
          message={
            guideIdToPlay === playerGuideId
              ? rejectReason || DISCORD_PLAYBACK_DISABLED_TEXT
              : DISCORD_PLAYBACK_DISABLED_TEXT
          }
          popoverClassName={css.popover}
          isOpen={isPopoverOpen}
        />
      )}
      <PlayButtonStateRepresentation
        id="playerActionButton"
        dataTestId={`player-status-${playerStatus?.toLowerCase()}`}
        className={classNames(css.playerPlayButton, className)}
        onClick={handlePlayButtonClick}
        fillButtonBackground={customFill}
        height={`${height}`}
        width={`${width}`}
      />
    </div>
  );
};

PlayButton.propTypes = {
  breakpoint: PropTypes.number.isRequired,
  enableDiscordControlTooltip: PropTypes.bool.isRequired,
  disablePreroll: PropTypes.bool,
  customFill: PropTypes.string,
  progressColor: PropTypes.string,
  onClick: PropTypes.func,
  guideItem: PropTypes.object,
  className: PropTypes.string,
  customHeight: PropTypes.number,
  customWidth: PropTypes.number,
};

export default connectWithPlayer(PlayButton);
